import { createReducer, on, Action } from '@ngrx/store';
import { ApiError, GetFareResponse } from '../../client/src';

import { FaresActions } from '../../actions';
import { LoadState } from '@nx-ng/types';
import { addFare, addFares } from '../../util';

export interface IFareTypeState {
  fares?: GetFareResponse;
  loadState: LoadState;
  errors?: ApiError | string;
}

export interface ISearchFareState extends IFareTypeState {
  fareLoadingCounter: number;
}

export interface IFaresState {
  flexi: IFareTypeState;
  payg: IFareTypeState;
  search: ISearchFareState;
}

export const initialState: IFaresState = {
  // set initial required properties
  flexi: {
    loadState: LoadState.NOT_LOADED,
  },
  payg: {
    loadState: LoadState.NOT_LOADED,
  },
  search: {
    fareLoadingCounter: 0,
    loadState: LoadState.NOT_LOADED,
  },
};

const faresReducer = createReducer<IFaresState, Action>(
  initialState,
  on(FaresActions.loadFlexiFares, (state: IFaresState) => ({
    ...state,
    flexi: {
      ...state.flexi,
      loadState: LoadState.LOADING,
      errors: undefined,
    },
  })),
  on(FaresActions.loadFlexiFaresSuccess, (state, { fareResponse }) => ({
    ...state,
    flexi: {
      ...state.flexi,
      fares: fareResponse,
      loadState: LoadState.LOADED,
    },
  })),
  on(FaresActions.loadFlexiFaresFailure, (state, { error }) => ({
    ...state,
    flexi: {
      ...state.flexi,
      errors: error,
      fares: undefined,
      loadState: LoadState.FAILED,
    },
  })),
  on(FaresActions.loadPaygFares, (state: IFaresState) => ({
    ...state,
    payg: {
      ...state.payg,
      loadState: LoadState.LOADING,
      errors: undefined,
    },
  })),
  on(FaresActions.loadPaygFaresSuccess, (state, { fareResponse }) => ({
    ...state,
    payg: {
      ...state.payg,
      fares: fareResponse,
      loadState: LoadState.LOADED,
    },
  })),
  on(FaresActions.loadPaygFaresFailure, (state, { error }) => ({
    ...state,
    payg: {
      ...state.payg,
      errors: error,
      fares: undefined,
      loadState: LoadState.FAILED,
    },
  })),
  on(FaresActions.searchFares, (state: IFaresState) => ({
    ...state,
    search: {
      ...state.search,
      loadState: LoadState.LOADING,
      errors: undefined,
    },
  })),
  on(FaresActions.searchFaresSuccess, (state, { fareResponse }) => ({
    ...state,
    search: {
      ...state.search,
      fares: addFares({
        currentFares: state.search.fares,
        newFares: fareResponse,
      }),
      loadState: LoadState.LOADED,
    },
  })),
  on(FaresActions.searchFaresFailure, (state, { error }) => ({
    ...state,
    search: {
      ...state.search,
      errors: error,
      loadState: LoadState.FAILED,
    },
  })),
  on(
    FaresActions.loadFareById,
    FaresActions.loadFareByAlias,
    (state: IFaresState) => ({
      ...state,
      search: {
        ...state.search,
        fareLoadingCounter: state.search.fareLoadingCounter + 1,
        loadState: LoadState.LOADING,
        errors: undefined,
      },
    })
  ),
  on(
    FaresActions.loadFareByIdSuccess,
    FaresActions.loadFareByAliasSuccess,
    (state, { fareResponse }) => ({
      ...state,
      search: {
        ...state.search,
        fareLoadingCounter: state.search.fareLoadingCounter - 1,
        fares: addFare({
          newFare: fareResponse,
          currentFares: state.search?.fares,
        }),
        loadState:
          state.search.fareLoadingCounter > 1
            ? LoadState.LOADING
            : LoadState.LOADED,
      },
    })
  ),
  on(
    FaresActions.loadFareByIdFailure,
    FaresActions.loadFareByALiasFailure,
    (state, { error }) => ({
      ...state,
      search: {
        ...state.search,
        fareLoadingCounter: state.search.fareLoadingCounter - 1,
        errors: error,
        loadState:
          state.search.fareLoadingCounter > 1
            ? LoadState.LOADING
            : LoadState.FAILED,
      },
    })
  )
);

export function reducer(state: IFaresState | undefined, action: Action) {
  return faresReducer(state, action);
}
